/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/** Callback run once the Usercentrics consent decision is known. */
export type ConsentReadyCallback = () => void;

/**
 * Payload of the `UC_UI_CMP_EVENT` events Usercentrics dispatches on `window`.
 *
 * `type` names the interaction (`CMP_SHOWN`, `ACCEPT_ALL`, …) and is absent on some events.
 */
export interface UsercentricsCmpEventDetail {
	type?: string;
}

/**
 * Waits for the Usercentrics consent decision before calling the callback.
 * Handles three scenarios:
 * 1. Returning visitor, UC already loaded — calls immediately
 * 2. Returning visitor, UC loads after mount — calls on UC_UI_INITIALIZED (no banner)
 * 3. First-time visitor — calls after UC_UI_CMP_EVENT (user interacts with banner)
 *
 * If Usercentrics is not present (dev env, script blocked), the popup simply won't show.
 *
 * @param callback
 */
export function onConsentReady(callback: ConsentReadyCallback): void {
	let called = false;
	const callOnce = () => {
		if (called) return;
		called = true;
		callback();
	};

	// UC already loaded and initialized (returning visitor, fast load)
	// Usercentrics installs itself as a browser global, so it has to be read off `window`
	// rather than imported; this gate only ever checks that it is there.
	if ((window as { UC_UI?: unknown }).UC_UI) {
		callOnce();
		return;
	}

	// Track whether the consent banner was shown (CMP_SHOWN fires before UC_UI_INITIALIZED)
	let bannerShown = false;

	// The looked-up value is nullable because `type` is absent on some CMP events.
	const CONSENT_ACTIONS: ReadonlyArray<string | undefined> = ['ACCEPT_ALL', 'DENY_ALL', 'SAVE'];
	window.addEventListener('UC_UI_CMP_EVENT', (event: Event) => {
		const cmpEvent = event as CustomEvent<UsercentricsCmpEventDetail | undefined>;

		if (cmpEvent.detail?.type === 'CMP_SHOWN') {
			bannerShown = true;
		}
		if (CONSENT_ACTIONS.includes(cmpEvent.detail?.type)) {
			callOnce();
		}
	});

	// When UC initializes: if banner was NOT shown, consent is already stored (returning visitor).
	// If banner WAS shown, wait for the user to interact (UC_UI_CMP_EVENT with consent action).
	window.addEventListener('UC_UI_INITIALIZED', () => {
		if (!bannerShown) {
			callOnce();
		}
	});
}
